import {Tr} from "@/i18n/i18n";
import {VerticalStandardPanel} from "@/scenes.ui/vertical-standard-panel";
import Scene = Phaser.Scene;
import TextUtils from "@/utils/text-utils";
import {Fonts} from "@/constants/font";
import {Keys} from "@/constants/key";
import {ImageButton} from "@/scenes.ui/image-button";
import {ConsumableSetting, ConsumableType, EffectCategory} from "@/store/consumable/types";
import ArtifactUtils from "@/utils/artifact-utils";
import {EffectType} from "@/store/artifact/types";
import TextButton from "@/scenes.ui/text-button";

export class ElixirPanel extends VerticalStandardPanel {

    private readonly DEPTH: number = 3000;

    constructor(scene: Scene, canvas: HTMLCanvasElement, cs: ConsumableSetting[], callback: (settings: ConsumableSetting[]) => unknown) {
        super(scene, canvas, Tr('cust'));
        this.setDepth(this.DEPTH);

        let slotLabel1 = TextUtils.addTextByFamily(scene,  Tr('slot') + ' 1', 0, -200,
            '#b3b3b3', 15, 'normal', 400, Fonts.MYRIADPRO_REGULAR);
        this.initTypeLine(scene, cs[0], -170);
        this.initEffectLine(scene, cs[0], -140);
        this.initCategoryLine(scene, cs[0], -110);

        let slotLabel2 = TextUtils.addTextByFamily(scene, Tr('slot') + ' 2', 0, -68,
            '#b3b3b3', 15, 'normal', 400, Fonts.MYRIADPRO_REGULAR);
        this.initTypeLine(scene, cs[1], -38);
        this.initEffectLine(scene, cs[1], -8);
        this.initCategoryLine(scene, cs[1], 22);

        let slotLabel3 = TextUtils.addTextByFamily(scene, Tr('slot') + ' 3', 0, 64,
            '#b3b3b3', 15, 'normal', 400, Fonts.MYRIADPRO_REGULAR);
        this.initTypeLine(scene, cs[2], 94);
        this.initEffectLine(scene, cs[2], 124);
        this.initCategoryLine(scene, cs[2], 154);

        this.add([slotLabel1, slotLabel2, slotLabel3])

        let self = this;
        let okButton = new TextButton(this.scene, 0, 220, Keys.UI_BUTTON_BROWN, Tr('conf'));
        okButton.setCallback(() => {
            if (this.isOnTop()) {
                callback(cs);
                self.closeWithCallback();
            }
        });
        this.add(okButton);
    }

    protected onClose(): void {

    }

    private initTypeLine(scene: Scene, cs: ConsumableSetting, yPos: number) {
        this.initLine(scene, Tr('type'),
            () => cs.consumableType = ElixirPanel.prev(cs.consumableType),
            () => cs.consumableType = ElixirPanel.next(cs.consumableType),
            () => ElixirPanel.typeToName(cs.consumableType), -105, yPos);
    }

    private initEffectLine(scene: Scene, cs: ConsumableSetting, yPos: number) {
        this.initLine(scene, Tr('effect'),
            () => cs.effectType = ElixirPanel.prevEffect(cs.effectType),
            () => cs.effectType = ElixirPanel.nextEffect(cs.effectType),
            () => ElixirPanel.effectToName(cs.effectType), -121, yPos);
    }

    private initCategoryLine(scene: Scene, cs: ConsumableSetting, yPos: number) {
        this.initLine(scene, Tr('power'),
            () => cs.effectCategory = ElixirPanel.prevCat(cs.effectCategory),
            () => cs.effectCategory = ElixirPanel.nextCat(cs.effectCategory),
            () => ElixirPanel.catToName(cs.effectCategory), -112, yPos);
    }

    private initLine(scene: Scene, name: string, prevCallback: () => unknown, nextCallback: () => unknown, textGetter: () => string, nameX: number, yPos: number) {
        let type = TextUtils.addTextByFamily(scene, name, nameX, yPos,
            '#dbdbdb', 12, 'normal', 400, Fonts.MYRIADPRO_REGULAR);
        let option = TextUtils.addTextByFamily(scene, textGetter(), 50, yPos,
            '#b1fd96', 14, 'normal', 400, Fonts.MYRIADPRO_REGULAR);
        const leftButton = new ImageButton(this.scene, -55, yPos, Keys.ATLAS_PROFILE, Keys.UI_ARROW_LEFT);
        const rightButton = new ImageButton(this.scene, 165, yPos, Keys.ATLAS_PROFILE, Keys.UI_ARROW_RIGHT);

        leftButton.setCallback(() => {
            prevCallback();
            option.setText(textGetter());
        });

        rightButton.setCallback(() => {
            nextCallback();
            option.setText(textGetter());
        });

        this.add([type, option, leftButton, rightButton])
    }

    // =====================================================

    private static typeToName(ct?: ConsumableType): string {
        if (ct === ConsumableType.PLAYER) {
            return Tr('eliksir').toUpperCase();
        } else if (ct === ConsumableType.ENEMY) {
            return Tr('scroll').toUpperCase();
        } else {
            return Tr('random');
        }
    }

    private static prev(ct?: ConsumableType): ConsumableType | undefined {
        if (ct === ConsumableType.PLAYER) {
            return undefined;
        } else if (ct === ConsumableType.ENEMY) {
            return ConsumableType.PLAYER;
        } else {
            return ConsumableType.ENEMY;
        }
    }

    private static next(ct?: ConsumableType): ConsumableType | undefined {
        if (ct === ConsumableType.PLAYER) {
            return ConsumableType.ENEMY;
        } else if (ct === ConsumableType.ENEMY) {
            return undefined;
        } else {
            return ConsumableType.PLAYER;
        }
    }

    // =====================================================

    private static effectToName(et?: EffectType): string {
        if (!et) {
            return Tr('random');
        }
        return ArtifactUtils.getEffectTypeText(et)!.toUpperCase();
    }

    private static prevEffect(et?: EffectType): EffectType | undefined {
        if (et === EffectType.ATTACK) {
            return undefined;
        } else if (et === EffectType.DEFENCE) {
            return EffectType.ATTACK;
        } else if (et === EffectType.HEALTH) {
            return EffectType.DEFENCE;
        } else {
            return EffectType.HEALTH;
        }
    }

    private static nextEffect(et?: EffectType): EffectType | undefined {
        if (et === EffectType.ATTACK) {
            return EffectType.DEFENCE;
        } else if (et === EffectType.DEFENCE) {
            return EffectType.HEALTH;
        } else if (et === EffectType.HEALTH) {
            return undefined;
        } else {
            return EffectType.ATTACK;
        }
    }

    // =====================================================

    private static catToName(ct?: EffectCategory): string {
        if (ct === EffectCategory.WEAKEST) {
            return Tr('weakest');
        } else if (ct === EffectCategory.STRONGEST) {
            return Tr('strongest');
        } else {
            return Tr('random');
        }
    }

    private static prevCat(ct?: EffectCategory): EffectCategory | undefined {
        if (ct === EffectCategory.STRONGEST) {
            return EffectCategory.ANY;
        } else if (ct === EffectCategory.WEAKEST) {
            return EffectCategory.STRONGEST;
        } else {
            return EffectCategory.WEAKEST;
        }
    }

    private static nextCat(ct?: EffectCategory): EffectCategory | undefined {
        if (ct === EffectCategory.STRONGEST) {
            return EffectCategory.WEAKEST;
        } else if (ct === EffectCategory.WEAKEST) {
            return EffectCategory.ANY;
        } else {
            return EffectCategory.STRONGEST;
        }
    }

}
